/**
 * ObjectivesAssignmentStep — paso 4 del setup wizard.
 *
 * Decide qué campañas del módulo /objectives pueden usar esta cuenta
 * para enviar mensajes. Sin esto, las Campañas no saben de qué número
 * disparar los envíos automáticos.
 */

import { useEffect, useState } from "react";
import {
    Loader2,
    Save,
    Target,
    CheckCircle2,
    Circle,
} from "lucide-react";
import { Button } from "../../../../shared/components/ui/Button";
import { whatsappAccountsApi } from "../../api";
import { objectivesApi } from "../../../objectives/api";
import type { WhatsAppAccount } from "../../types";
import type { Objective } from "../../../objectives/types";

interface Props {
    account: WhatsAppAccount;
    onComplete: (updated: WhatsAppAccount) => void;
}

export default function ObjectivesAssignmentStep({ account, onComplete }: Props) {
    const [objectives, setObjectives] = useState<Objective[]>([]);
    const [loading, setLoading] = useState(true);
    const [selected, setSelected] = useState<Set<string>>(
        new Set(account.assignedObjectives || []),
    );
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        (async () => {
            setLoading(true);
            const res = await objectivesApi.list("brand-demo");
            if (res.success) setObjectives(res.data);
            setLoading(false);
        })();
    }, []);

    const toggle = (id: string) => {
        setSelected((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    };

    const handleSave = async () => {
        setSaving(true);
        setError(null);
        const res = await whatsappAccountsApi.assignObjectives(
            account.id,
            Array.from(selected),
        );
        setSaving(false);
        if (!res.success) {
            setError(res.error || "Error al asignar");
            return;
        }
        onComplete(res.data);
    };

    return (
        <div className="space-y-5">
            <div className="flex items-start gap-3">
                <div className="p-2 rounded-lg bg-violet-50 text-violet-600">
                    <Target size={16} />
                </div>
                <div>
                    <h3 className="text-base font-bold text-slate-900">
                        Asignar a campañas
                    </h3>
                    <p className="text-xs text-slate-500 mt-0.5 leading-relaxed">
                        Marcá qué campañas automáticas pueden usar este número para
                        enviar mensajes. Podés dejarla sin asignar y elegir manualmente
                        en cada envío.
                    </p>
                </div>
            </div>

            {loading ? (
                <div className="flex items-center justify-center py-12 text-slate-400 text-xs">
                    <Loader2 size={14} className="animate-spin mr-2" />
                    Cargando campañas…
                </div>
            ) : objectives.length === 0 ? (
                <div className="bg-slate-50 border border-slate-200 rounded-xl p-6 text-center text-xs text-slate-500">
                    Todavía no creaste campañas. Después podés volver a este paso
                    desde la pantalla de la cuenta.
                </div>
            ) : (
                <div className="space-y-2">
                    {objectives.map((obj) => {
                        const isSelected = selected.has(obj.id);
                        return (
                            <button
                                key={obj.id}
                                onClick={() => toggle(obj.id)}
                                className={`w-full text-left p-3 rounded-xl border-2 transition-all flex items-center gap-3 ${
                                    isSelected
                                        ? "border-green-500 bg-green-50/50"
                                        : "border-slate-200 bg-white hover:border-slate-300"
                                }`}
                            >
                                <div className="text-2xl flex-shrink-0">
                                    {obj.emoji}
                                </div>
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2 mb-0.5">
                                        <span className="text-sm font-semibold text-slate-900 truncate">
                                            {obj.name}
                                        </span>
                                        <span
                                            className={`text-[9px] font-bold uppercase px-1.5 py-0.5 rounded ${
                                                obj.status === "active"
                                                    ? "bg-emerald-100 text-emerald-700"
                                                    : obj.status === "paused"
                                                      ? "bg-slate-100 text-slate-600"
                                                      : "bg-amber-100 text-amber-700"
                                            }`}
                                        >
                                            {obj.status}
                                        </span>
                                    </div>
                                    <p className="text-[11px] text-slate-500">
                                        {(obj.metrics?.peopleReached || 0).toLocaleString(
                                            "es-AR",
                                        )}{" "}
                                        contactos alcanzados ·{" "}
                                        {obj.metrics?.conversions || 0} conversiones
                                    </p>
                                </div>
                                {isSelected ? (
                                    <CheckCircle2
                                        size={18}
                                        className="text-green-600 flex-shrink-0"
                                    />
                                ) : (
                                    <Circle
                                        size={18}
                                        className="text-slate-300 flex-shrink-0"
                                    />
                                )}
                            </button>
                        );
                    })}
                </div>
            )}

            {error && (
                <div className="text-xs text-red-600 bg-red-50 border border-red-100 rounded-lg p-2.5">
                    {error}
                </div>
            )}

            <div className="flex items-center justify-between">
                <p className="text-[11px] text-slate-500">
                    {selected.size === 0
                        ? "Ninguna campaña asignada — vas a poder asignarlas manualmente en cada envío."
                        : `${selected.size} campaña${selected.size > 1 ? "s" : ""} asignada${
                              selected.size > 1 ? "s" : ""
                          }`}
                </p>
                <Button
                    variant="primary"
                    onClick={handleSave}
                    disabled={saving}
                >
                    {saving ? (
                        <Loader2 size={14} className="animate-spin" />
                    ) : (
                        <Save size={14} />
                    )}
                    Guardar y continuar
                </Button>
            </div>
        </div>
    );
}
